import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { cn } from "@/lib/cn";
import { CheckCircle, AlertTriangle, XCircle, Info, X, Undo2 } from "lucide-react";

type ToastVariant = "success" | "error" | "warning" | "info";

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastItem {
  id: number;
  variant: ToastVariant;
  message: string;
  action?: ToastAction;
}

interface ShowToastOptions {
  action?: ToastAction;
  /** Auto-dismiss delay in ms. Defaults to 5000. Pass 0 to disable. */
  duration?: number;
}

interface ToastContextValue {
  success: (message: string, options?: ShowToastOptions) => void;
  error: (message: string, options?: ShowToastOptions) => void;
  warning: (message: string, options?: ShowToastOptions) => void;
  info: (message: string, options?: ShowToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const variantConfig: Record<
  ToastVariant,
  { icon: typeof CheckCircle; iconClass: string; barClass: string }
> = {
  success: { icon: CheckCircle, iconClass: "text-green-600", barClass: "bg-green-500" },
  error: { icon: XCircle, iconClass: "text-red-600", barClass: "bg-red-500" },
  warning: { icon: AlertTriangle, iconClass: "text-amber-600", barClass: "bg-amber-500" },
  info: { icon: Info, iconClass: "text-ceramic-700", barClass: "bg-ceramic-900" },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (variant: ToastVariant, message: string, options?: ShowToastOptions) => {
      const id = ++idRef.current;
      const duration = options?.duration ?? 5000;
      setToasts((prev) => [...prev, { id, variant, message, action: options?.action }]);
      if (duration > 0) {
        window.setTimeout(() => dismiss(id), duration);
      }
    },
    [dismiss]
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      success: (message, options) => show("success", message, options),
      error: (message, options) => show("error", message, options),
      warning: (message, options) => show("warning", message, options),
      info: (message, options) => show("info", message, options),
    }),
    [show]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 w-[360px] max-w-[calc(100vw-3rem)]"
        role="region"
        aria-label="通知"
      >
        {toasts.map((toast) => {
          const config = variantConfig[toast.variant];
          const Icon = config.icon;
          return (
            <div
              key={toast.id}
              role={toast.variant === "error" ? "alert" : "status"}
              aria-live={toast.variant === "error" ? "assertive" : "polite"}
              className="relative flex items-start gap-3 overflow-hidden rounded-xl border border-ceramic-200 bg-white p-4 shadow-sm"
            >
              <span className={cn("absolute inset-y-0 left-0 w-1", config.barClass)} aria-hidden />
              <Icon className={cn("mt-0.5 h-5 w-5 flex-shrink-0", config.iconClass)} aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="break-words text-sm text-ceramic-700">{toast.message}</p>
                {toast.action && (
                  <button
                    type="button"
                    onClick={() => {
                      toast.action?.onClick();
                      dismiss(toast.id);
                    }}
                    className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-ceramic-900 hover:underline"
                  >
                    <Undo2 className="h-3.5 w-3.5" />
                    {toast.action.label}
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                aria-label="關閉通知"
                className="flex-shrink-0 rounded p-1 text-ceramic-400 hover:bg-ceramic-100 hover:text-ceramic-700 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return ctx;
}
